import React, { useEffect, useRef, useCallback, useState } from 'react';
import { animated } from 'react-spring';
import { useQueryClient } from '@tanstack/react-query';
import { useAppStore, useUI, useFilters } from '@/store';
import { LoadingScreen, Button } from '@/components/ui';
import { TelegramUtils } from '@/utils/telegram';
import { api } from '@/api';
import { cn } from '@/utils';
import { BrowseCardResponse } from '@/types';

import logoLight from '@/assets/itinder.svg';
import logoDark from '@/assets/itinder_dark.svg';
import matchIconLight from '@/assets/match.svg';
import matchIconDark from '@/assets/match_dark.svg';
import profileIconLight from '@/assets/profile.svg';
import profileIconDark from '@/assets/profile_dark.svg';
import filterIcon from '@/assets/filter.svg';
import filterIconWhite from '@/assets/filter_white.svg';

import VerticalFeedCard from '@/components/cards/VerticalFeedCard';
import ProfileDetailScreen from './ProfileDetailScreen';

const AlternativeFeedScreen: React.FC = () => {
  const { 
    verticalFeed, 
    setVerticalFeedProfiles, 
    addVerticalFeedProfiles,
    setVerticalFeedLastId,
    setVerticalFeedLoading,
    setVerticalFeedHasMore,
    updateVerticalFeedProfile,
    toggleFeedType
  } = useAppStore();
  
  const { setActiveScreen } = useUI();
  const { hasActiveFilters } = useFilters();
  const [viewingProfileId, setViewingProfileId] = useState<string | null>(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isPullRefreshing, setIsPullRefreshing] = useState(false);
  
  const observerTarget = useRef<HTMLDivElement>(null);
  const feedContainerRef = useRef<HTMLDivElement>(null);
  
  // Select correct icons based on theme
  const logo = TelegramUtils.isLightTheme ? logoDark : logoLight;
  const matchIcon = TelegramUtils.isLightTheme ? matchIconDark : matchIconLight;
  const profileIcon = TelegramUtils.isLightTheme ? profileIconDark : profileIconLight;
  const filterIconSrc = TelegramUtils.isLightTheme ? filterIconWhite : filterIcon;

  // Configure Telegram UI
  useEffect(() => {
    TelegramUtils.configureBackButton(false);
    TelegramUtils.configureMainButton({ visible: false });
  }, []);

  // Load initial profiles
  const loadInitialProfiles = useCallback(async () => {
    if (verticalFeed.loading) return;
    
    setVerticalFeedLoading(true);
    setIsInitialLoad(true);
    
    try {
      const profiles = await api.browseProfiles(20);
      
      if (profiles && profiles.length > 0) {
        setVerticalFeedProfiles(profiles);
        const lastProfile = profiles[profiles.length - 1];
        setVerticalFeedLastId(lastProfile.id);
        setVerticalFeedHasMore(profiles.length === 20);
      } else {
        setVerticalFeedProfiles([]);
        setVerticalFeedHasMore(false);
      }
    } catch (error) {
      console.error('Failed to load initial profiles:', error);
      TelegramUtils.haptic.error();
    } finally {
      setVerticalFeedLoading(false);
      setIsInitialLoad(false);
    }
  }, [verticalFeed.loading]);

  // Load more profiles (pagination)
  const loadMoreProfiles = useCallback(async () => {
    if (verticalFeed.loading || !verticalFeed.hasMore || isLoadingMore) return;
    
    setIsLoadingMore(true);
    
    try {
      const profiles = await api.browseProfiles(20, verticalFeed.lastId || undefined);
      
      if (profiles && profiles.length > 0) {
        addVerticalFeedProfiles(profiles);
        const lastProfile = profiles[profiles.length - 1];
        setVerticalFeedLastId(lastProfile.id);
        setVerticalFeedHasMore(profiles.length === 20);
      } else {
        setVerticalFeedHasMore(false);
      }
    } catch (error) {
      console.error('Failed to load more profiles:', error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [verticalFeed.loading, verticalFeed.hasMore, verticalFeed.lastId, isLoadingMore]);

  // Pull to refresh
  const handlePullToRefresh = useCallback(async () => {
    setIsPullRefreshing(true);
    await loadInitialProfiles();
    setIsPullRefreshing(false);
  }, [loadInitialProfiles]);

  // Handle like action
  const handleLikeToggle = useCallback(async (profile: BrowseCardResponse) => {
    try {
      if (profile.swipe_status === 'like') {
        // Unlike - delete the like
        await api.deleteLike(profile.id);
        updateVerticalFeedProfile(profile.id, null);
        TelegramUtils.haptic.light();
      } else {
        // Like - update swipe to like
        const result = await api.updateSwipe(profile.id, 'like');
        updateVerticalFeedProfile(profile.id, 'like');
        TelegramUtils.haptic.success();
        
        if (result.is_match) {
          // Show match celebration
          TelegramUtils.haptic.success();
          await TelegramUtils.showAlert('🎉 It\'s a Match!');
        }
      }
    } catch (error) {
      console.error('Failed to update like:', error);
      TelegramUtils.haptic.error();
    }
  }, [updateVerticalFeedProfile]);

  // Handle hide action
  const handleHide = useCallback(async (profile: BrowseCardResponse) => {
    try {
      // Call API with hide parameter
      await api.updateSwipe(profile.id, 'pass', 1);
      
      // Remove profile from feed
      setVerticalFeedProfiles(verticalFeed.profiles.filter(p => p.id !== profile.id));
      
      TelegramUtils.haptic.medium();
    } catch (error) {
      console.error('Failed to hide profile:', error);
      TelegramUtils.haptic.error();
      await TelegramUtils.showAlert('Не удалось скрыть профиль');
    }
  }, [verticalFeed.profiles, setVerticalFeedProfiles]);

  // Load initial profiles on mount
  useEffect(() => {
    if (verticalFeed.profiles.length === 0 && !verticalFeed.loading) {
      loadInitialProfiles();
    }
  }, []);

  // Set up intersection observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && verticalFeed.hasMore && !isLoadingMore) {
          loadMoreProfiles();
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => observer.disconnect();
  }, [verticalFeed.hasMore, isLoadingMore, loadMoreProfiles]);

  // Handle pull-to-refresh gesture
  useEffect(() => {
    let startY = 0;
    let currentY = 0;
    let isPulling = false;

    const handleTouchStart = (e: TouchEvent) => {
      if (feedContainerRef.current?.scrollTop === 0) {
        startY = e.touches[0].clientY;
        isPulling = true;
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!isPulling) return;
      currentY = e.touches[0].clientY;
      const deltaY = currentY - startY;
      
      if (deltaY > 0 && feedContainerRef.current?.scrollTop === 0) {
        e.preventDefault();
        // Visual feedback for pull
        if (deltaY > 60 && !isPullRefreshing) {
          TelegramUtils.haptic.light();
        }
      }
    };

    const handleTouchEnd = () => {
      const deltaY = currentY - startY;
      if (isPulling && deltaY > 60 && !isPullRefreshing) {
        handlePullToRefresh();
      }
      isPulling = false;
      startY = 0;
      currentY = 0;
    };

    const container = feedContainerRef.current;
    if (container) {
      container.addEventListener('touchstart', handleTouchStart, { passive: true });
      container.addEventListener('touchmove', handleTouchMove, { passive: false });
      container.addEventListener('touchend', handleTouchEnd);
    }

    return () => {
      if (container) {
        container.removeEventListener('touchstart', handleTouchStart);
        container.removeEventListener('touchmove', handleTouchMove);
        container.removeEventListener('touchend', handleTouchEnd);
      }
    };
  }, [isPullRefreshing, handlePullToRefresh]);

  // Show loading screen for initial load
  if (isInitialLoad && verticalFeed.loading) {
    return <LoadingScreen message="Загружаем профили..." />;
  }

  // Show profile detail if viewing
  if (viewingProfileId) {
    return (
      <ProfileDetailScreen
        userId={viewingProfileId}
        onBack={() => setViewingProfileId(null)}
        onSwipe={(direction) => {
          // Handle swipe from detail view if needed
          setViewingProfileId(null);
        }}
      />
    );
  }

  return (
    <div className="fixed inset-0 bg-black flex flex-col">
      {/* Header - Fixed at top */}
      <div className="flex-shrink-0 bg-black/95 backdrop-blur-xl border-b border-white/10 safe-area-top z-50">
        <div className="flex items-center justify-between py-3 px-6">
          {/* Logo - Clickable to toggle feed type */}
          <button
            onClick={() => {
              toggleFeedType();
              TelegramUtils.haptic.selection();
            }}
            className="hover:opacity-80 transition-opacity"
          >
            <img 
              src={logo} 
              alt="ITinder" 
              className="h-7 w-auto"
            />
          </button>
          
          {/* Icons */}
          <div className="flex items-center gap-2">
            {/* Filter icon */}
            <button
              onClick={() => {
                setActiveScreen('filters');
                TelegramUtils.haptic.selection();
              }}
              className="relative h-7 w-7 flex items-center justify-center hover:opacity-80 transition-opacity"
            >
              <img 
                src={filterIconSrc} 
                alt="Filters" 
                className="h-6 w-6"
              />
              {hasActiveFilters && (
                <div className="absolute top-0 right-0 w-2 h-2 bg-primary-500 rounded-full border border-tg-bg" />
              )}
            </button>
            
            {/* Messages icon */}
            <button
              onClick={() => {
                setActiveScreen('matches');
                TelegramUtils.haptic.selection();
              }}
              className="relative h-7 w-7 flex items-center justify-center hover:opacity-80 transition-opacity"
            >
              <img 
                src={matchIcon} 
                alt="Matches" 
                className="h-7 w-7"
              />
            </button>
            
            {/* Profile icon */}
            <button
              onClick={() => {
                setActiveScreen('profile');
                TelegramUtils.haptic.selection();
              }}
              className="h-7 w-7 flex items-center justify-center hover:opacity-80 transition-opacity"
            >
              <img 
                src={profileIcon} 
                alt="Profile" 
                className="h-6 w-6"
              />
            </button>
          </div>
        </div>
      </div>

      {/* Feed Container */}
      <div 
        ref={feedContainerRef}
        className="flex-1 overflow-y-auto pb-safe bg-black"
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        {/* Pull to refresh indicator */}
        {isPullRefreshing && (
          <div className="flex justify-center py-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white" />
          </div>
        )}

        {/* Empty State */}
        {verticalFeed.profiles.length === 0 && !verticalFeed.loading && (
          <div className="flex flex-col items-center justify-center px-4 py-16 text-center min-h-[80vh] bg-black">
            <div className="w-full max-w-sm">
              {/* Animated Icon */}
              <div className="relative mb-8">
                <div className="w-32 h-32 mx-auto bg-gradient-to-br from-white/10 to-white/5 rounded-full flex items-center justify-center backdrop-blur-sm">
                  <svg className="w-16 h-16 text-white/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                </div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-36 h-36 border border-white/10 rounded-full animate-ping" />
                </div>
              </div>
              
              <h2 className="text-3xl font-bold text-white mb-3">
                Все просмотрено
              </h2>
              <p className="text-white/60 text-base mb-8 leading-relaxed">
                Вы посмотрели все доступные профили. Новые анкеты появятся совсем скоро!
              </p>
              
              <div className="space-y-3">
                <button
                  onClick={() => {
                    loadInitialProfiles();
                  }}
                  className="w-full h-14 bg-white text-black rounded-2xl font-semibold text-base hover:bg-neutral-100 transition-all active:scale-95"
                >
                  Обновить ленту
                </button>
                
                <button
                  onClick={() => {
                    toggleFeedType();
                    TelegramUtils.haptic.selection();
                  }}
                  className="w-full h-14 bg-white/10 backdrop-blur-sm text-white rounded-2xl font-medium text-base hover:bg-white/20 transition-all active:scale-95 border border-white/20"
                >
                  Вернуться к карточкам
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Profile Cards */}
        <div>
          {verticalFeed.profiles.map((profile, index) => (
            <animated.div
              key={profile.id}
              style={{
                opacity: 1,
                transform: 'translateY(0)',
              }}
              className={`transition-all duration-300 ${index < verticalFeed.profiles.length - 1 ? 'mb-3' : ''}`}
            >
              <VerticalFeedCard
                profile={profile}
                onLikeToggle={() => handleLikeToggle(profile)}
                onHide={() => handleHide(profile)}
                onCardClick={() => setViewingProfileId(profile.id.toString())}
              />
            </animated.div>
          ))}
        </div>

        {/* Loading More Indicator */}
        {isLoadingMore && (
          <div className="flex justify-center py-6">
            <div className="flex items-center gap-3 text-white/60">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white/60" />
              <span className="text-sm">Загружаем больше профилей...</span>
            </div>
          </div>
        )}

        {/* Intersection Observer Target */}
        <div ref={observerTarget} className="h-1" />

        {/* No More Profiles */}
        {!verticalFeed.hasMore && verticalFeed.profiles.length > 0 && (
          <div className="text-center py-8 text-white/40">
            <p className="text-sm">Вы посмотрели все доступные профили</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AlternativeFeedScreen;
